/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import React, { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { ExtendedFetchBaseQueryError } from "common/types/ExtendedFetchBaseQueryError";
import { NotificationMessage } from "common/types/Notification";
import { dequeueNotification } from "store/slices/notification";
import { useCommonState } from "common/hooks";
import { getErrorName } from "common/util";
import { DayDefinition } from "common/types/Configuration";

interface ErrorMessageError extends Error {
    data?: any;
}

export interface ErrorMessageProps {
    error: ErrorMessageError | ExtendedFetchBaseQueryError;
    fallback?: ReactElement;
    message?: string;
}

export default function ErrorMessage({
    error,
    fallback,
    message,
}: ErrorMessageProps): ReactElement {
    const errorCode = error?.data?.error_code;
    message = errorCode ? errorCode : message ?? getErrorName(error);
    const metadata = (error as ExtendedFetchBaseQueryError as any)?.data;
    const { configuration } = useCommonState();
    const hoursConstraints = configuration?.shiftsConfig.hoursConstraints as any;

    const periodDay = <FormattedMessage id="shift.period.day" defaultMessage="a day" />;
    const period24Hour = (
        <FormattedMessage id="shift.period.24hour" defaultMessage="a 24-hour period" />
    );
    const periodWeek = <FormattedMessage id="shift.period.week" defaultMessage="a week" />;

    if (message === NotificationMessage.DailyClaimLimitExceeded) {
        if (metadata?.ok) {
            //to be error.data.metadata
            if (metadata.claimed > 0) {
                return (
                    <FormattedMessage
                        id="shift.claimLimitExceeded"
                        defaultMessage='Successfully reserved {hoursClaimed} of {hoursRequested} requested hours due to the maximum allowed driving duration of {hoursLimit} hours in {period}. Tap "Close" to see your reserved slots.'
                        values={{
                            hoursClaimed: metadata.claimed,
                            hoursLimit: hoursConstraints?.dailyHoursLimit,
                            hoursRequested: metadata.requested,
                            period:
                                hoursConstraints?.dayDefinition === DayDefinition.Rolling24
                                    ? period24Hour
                                    : periodDay,
                        }}
                    />
                );
            }

            return (
                <FormattedMessage
                    id="shift.claimLimitExceededNoClaims"
                    defaultMessage="Unable to reserve any of the {hoursRequested} requested hours due to the maximum allowed driving duration of {hoursLimit} hours in {period}."
                    values={{
                        hoursLimit: hoursConstraints?.dailyHoursLimit,
                        hoursRequested: metadata.requested,
                        period:
                            hoursConstraints?.dayDefinition === DayDefinition.Rolling24
                                ? period24Hour
                                : periodDay,
                    }}
                />
            );
        }
        return (
            <FormattedMessage
                id="shift.claimLimitExceededSimple"
                defaultMessage='One or more of your selection exceeds the maximum allowed driving duration of {hoursLimit} hours in {period}. Tap "Close" to see your reserved slots.'
                values={{
                    hoursLimit: hoursConstraints?.dailyHoursLimit,
                    period:
                        hoursConstraints?.dayDefinition === DayDefinition.Rolling24
                            ? period24Hour
                            : periodDay,
                }}
            />
        );
    }

    if (message === NotificationMessage.WeeklyClaimLimitExceeded) {
        if (metadata?.ok) {
            if (metadata.claimed > 0) {
                return (
                    <FormattedMessage
                        id="shift.claimLimitExceeded"
                        defaultMessage='Successfully reserved {hoursClaimed} of {hoursRequested} requested hours due to the maximum allowed driving duration of {hoursLimit} hours in {period}. Tap "Close" to see your reserved slots.'
                        values={{
                            hoursClaimed: metadata.claimed,
                            hoursLimit: hoursConstraints?.weeklyHoursLimit,
                            hoursRequested: metadata.requested,
                            period: periodWeek,
                        }}
                    />
                );
            }
            return (
                <FormattedMessage
                    id="shift.claimLimitExceededNoClaims"
                    defaultMessage="Unable to reserve any of the {hoursRequested} requested hours due to the maximum allowed driving duration of {hoursLimit} hours in {period}."
                    values={{
                        hoursLimit: hoursConstraints?.weeklyHoursLimit,
                        hoursRequested: metadata?.requested,
                        period: periodWeek,
                    }}
                />
            );
        }
        return (
            <FormattedMessage
                id="shift.claimLimitExceededSimple"
                defaultMessage='One or more of your selection exceeds the maximum allowed driving duration of {hoursLimit} hours in {period}. Tap "Close" to see your reserved slots.'
                values={{
                    hoursLimit: hoursConstraints?.weeklyHoursLimit,
                    period: periodWeek,
                }}
            />
        );
    }

    if (message === NotificationMessage.TooManyShiftsPerDay) {
        if (metadata?.ok) {
            if (metadata.claimed > 0) {
                return (
                    <FormattedMessage
                        id="shift.tooManyShiftsPerPeriod"
                        defaultMessage='Successfully reserved {shiftsClaimed} of {shiftsRequested} requested shifts due to the maximum allowed {shiftsLimit} shifts in {period}. Tap "Close" to see your reserved slots.'
                        values={{
                            period:
                                hoursConstraints?.dayDefinition === DayDefinition.Rolling24
                                    ? period24Hour
                                    : periodDay,
                            shiftsClaimed: metadata.claimed,
                            shiftsLimit: hoursConstraints?.dailyShiftsLimit,
                            shiftsRequested: metadata.requested,
                        }}
                    />
                );
            }
            return (
                <FormattedMessage
                    id="shift.tooManyShiftsPerPeriodNoClaims"
                    defaultMessage="Unable to reserve any of the {shiftsRequested} requested shifts due to the maximum allowed {shiftsLimit} shifts in {period}."
                    values={{
                        period:
                            hoursConstraints?.dayDefinition === DayDefinition.Rolling24
                                ? period24Hour
                                : periodDay,
                        shiftsLimit: hoursConstraints?.dailyShiftsLimit,
                        shiftsRequested: metadata.requested,
                    }}
                />
            );
        }
        return (
            <FormattedMessage
                id="shift.tooManyShiftsPerPeriodSimple"
                defaultMessage="Sorry, there's a limit to how many shifts you can reserve: up to {shiftsLimit} shifts in {period}."
                values={{
                    period:
                        hoursConstraints?.dayDefinition === DayDefinition.Rolling24
                            ? period24Hour
                            : periodDay,
                    shiftsLimit: hoursConstraints?.dailyShiftsLimit,
                }}
            />
        );
    }

    if (message === NotificationMessage.TooManyShiftsPerWeek) {
        if (metadata?.ok) {
            if (metadata?.claimed > 0) {
                return (
                    <FormattedMessage
                        id="shift.tooManyShiftsPerPeriod"
                        defaultMessage='Successfully reserved {shiftsClaimed} of {shiftsRequested} requested shifts due to the maximum allowed {shiftsLimit} shifts in {period}. Tap "Close" to see your reserved slots.'
                        values={{
                            period: periodWeek,
                            shiftsClaimed: metadata.claimed,
                            shiftsLimit: hoursConstraints?.weeklyShiftsLimit,
                            shiftsRequested: metadata.requested,
                        }}
                    />
                );
            }

            return (
                <FormattedMessage
                    id="shift.tooManyShiftsPerPeriodNoClaims"
                    defaultMessage="Unable to reserve any of the {shiftsRequested} requested shifts due to the maximum allowed {shiftsLimit} shifts in {period}."
                    values={{
                        period: periodWeek,
                        shiftsLimit: hoursConstraints?.weeklyShiftsLimit,
                        shiftsRequested: metadata.requested,
                    }}
                />
            );
        }

        return (
            <FormattedMessage
                id="shift.tooManyShiftsPerPeriodSimple"
                defaultMessage="Sorry, there's a limit to how many shifts you can reserve: up to {shiftsLimit} shifts in {period}."
                values={{ period: periodWeek, shiftsLimit: hoursConstraints?.weeklyShiftsLimit }}
            />
        );
    }

    if (message === NotificationMessage.TooLittleRestBetweenShifts) {
        if (metadata?.ok) {
            if (metadata?.claimed > 0) {
                return (
                    <FormattedMessage
                        id="shift.tooLittleRestBetweenShifts"
                        defaultMessage='Successfully reserved {shiftsClaimed} of {shiftsRequested} requested shifts due to the minimum allowed {restBetweenShifts} hours rest between shifts. Tap "Close" to see your reserved slots.'
                        values={{
                            restBetweenShifts: hoursConstraints?.restBetweenShifts,
                            shiftsClaimed: metadata.claimed,
                            shiftsRequested: metadata.requested,
                        }}
                    />
                );
            }

            return (
                <FormattedMessage
                    id="shift.tooLittleRestBetweenShiftsNoClaims"
                    defaultMessage="Unable to reserve any of the {shiftsRequested} requested shifts due to the minimum allowed {restBetweenShifts} hours rest between shifts."
                    values={{
                        restBetweenShifts: hoursConstraints.restBetweenShifts,
                        shiftsRequested: metadata.requested,
                    }}
                />
            );
        }

        return (
            <FormattedMessage
                id="shift.tooLittleRestBetweenShiftsSimple"
                defaultMessage="Sorry, some of the shifts you've reserved have too little rest between them. The minimum allowed rest between shifts is {restBetweenShifts} hours."
                values={{
                    restBetweenShifts: hoursConstraints?.restBetweenShifts,
                }}
            />
        );
    }

    //case of no matadata
    if (message === NotificationMessage.BatchNotAvailable) {
        return (
            <FormattedMessage
                id="common.error.batchNotAvailable"
                defaultMessage="The shift is either in the past or doesn't exist anymore."
            />
        );
    }

    if (message === NotificationMessage.LateAbsenceRequest) {
        return (
            <FormattedMessage
                id="common.error.lateAbsenceRequest"
                defaultMessage="If you will not be available in this timeframe, please <link>Contact Us</link>."
                values={{
                    link: (contactUsPage: string) => (
                        <Link to="/contact-us" onClick={() => dequeueNotification()}>
                            {contactUsPage}
                        </Link>
                    ),
                }}
            />
        );
    }

    if (message === NotificationMessage.LateShiftCancellationRequest) {
        return (
            <FormattedMessage
                id="common.error.LateShiftCancellationRequest"
                defaultMessage="If you will not be available in this timeframe, please <link>Contact Us</link>."
                values={{
                    link: (contactUsPage: string) => (
                        <Link to="/contact-us" onClick={() => dequeueNotification()}>
                            {contactUsPage}
                        </Link>
                    ),
                }}
            />
        );
    }

    if (message === NotificationMessage.OverlappingClaim) {
        return (
            <FormattedMessage
                id="shift.claimOverlaps"
                defaultMessage="One or more of your selections could not be claimed because of a conflict with your current driving schedule."
            />
        );
    }

    if (
        message === NotificationMessage.ShiftAlreadyClaimedByAnotherDriver ||
        message === NotificationMessage.ShiftAlreadyClaimedByOtherDriver
    ) {
        return (
            <FormattedMessage
                id="shift.claimConflict"
                defaultMessage="One or more of your selected times were already reserved by someone else. Please try again."
            />
        );
    }

    if (message === NotificationMessage.JWTValidationError) {
        return (
            <FormattedMessage
                id="common.error.jwtValidationError"
                defaultMessage="Invalid or expired token."
            />
        );
    }
    if (typeof message === "string") {
        if (message && message.indexOf(NotificationMessage.NoMatchingClaimFlowServiceTags) !== -1) {
            return (
                <FormattedMessage
                    id="common.error.noMatchingClaimServiceTags"
                    defaultMessage="Your account has not been configured correctly, and may be missing a tag. Please contact support."
                />
            );
        }
    }

    if (
        message === NotificationMessage.DriverNotAuthenticated ||
        message === NotificationMessage.DriverNotAuthorized
    ) {
        return (
            <FormattedMessage
                id="common.error.driverSessionHasExpired"
                defaultMessage="Your session has expired, please revisit the page and try again."
            />
        );
    }

    if (message === NotificationMessage.MixedDriverVehicle) {
        return (
            <FormattedMessage
                id="common.error.MixedDriverVehicle"
                defaultMessage="Looks like your Fleetster account may not be set up correctly. Please contact support."
            />
        );
    }

    if (message === NotificationMessage.CantUnclaimPastShift) {
        return (
            <FormattedMessage
                id="shift.unclaimPastShift"
                defaultMessage="Unable to cancel a past reservation."
            />
        );
    }

    if (message === NotificationMessage.DomainNotAccessible) {
        return (
            <FormattedMessage
                defaultMessage="We don’t recognize your login. Make sure to log into the platform you signed up for, with the correct phone number."
                id="common.error.driverNotAuthenticated"
            />
        );
    }
    if (message === NotificationMessage.InvalidCode) {
        return (
            <FormattedMessage
                defaultMessage="There's something wrong with your code. Please try again or request a new code."
                id="common.error.invalidCode"
            />
        );
    }
    if (
        message === NotificationMessage.DriverInActive ||
        message === NotificationMessage.UnsupportedCity
    ) {
        return (
            <FormattedMessage
                id="common.error.driverInActive"
                defaultMessage="It looks like your access to the Via Platform is restricted. Please contact support for more details and instructions."
            />
        );
    }
    if (message === NotificationMessage.DriverNotFound) {
        return (
            <FormattedMessage
                defaultMessage="Hm, we don't recognize this number. Make sure to use the phone number you signed up with."
                id="common.error.userNotFound"
            />
        );
    }
    if (message === NotificationMessage.OverlappingAbsenceRequest) {
        return (
            <FormattedMessage
                defaultMessage="The time you have selected is overlapping with an existing request. Please select a different time."
                id="common.error.overlappingAbsenceRequest"
            />
        );
    }

    if (message === NotificationMessage.AbsenceDuringOAError) {
        return (
            <FormattedMessage
                defaultMessage="Cannot create or modify absence. Please try again later."
                id="common.error.createEditAbsenceError"
            />
        );
    }

    if (fallback) {
        return fallback;
    }

    return <FormattedMessage id="common.error.system" defaultMessage="System error" />;
}
